import { useEffect, useMemo, useRef, useState } from 'react'
import { useVeil } from '../lib/useVeil'
import { maskName } from '../lib/security'
import { api } from '../lib/api'
import type { Person } from '../lib/types'

/**
 * Atlas — where the family walked the earth.
 * Dependency-free slippy map: OpenStreetMap raster tiles + Web-Mercator math,
 * markers for birth (gold), death (violet), and resting places (silver).
 * Geocoding is server-cached (Nominatim behind /api/geocode).
 */

const TILE = 256

interface Marker {
  lat: number
  lon: number
  kind: 'birth' | 'death' | 'burial'
  label: string
  place: string
}

const KIND_STYLE: Record<Marker['kind'], { color: string; icon: string }> = {
  birth: { color: '#e8bc6a', icon: '🌅' },
  death: { color: '#a78bfa', icon: '🕯️' },
  burial: { color: '#cbd5e1', icon: '🕊️' },
}

function lonToX(lon: number, z: number) { return ((lon + 180) / 360) * Math.pow(2, z) * TILE }
function latToY(lat: number, z: number) {
  const r = (lat * Math.PI) / 180
  return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * Math.pow(2, z) * TILE
}

export default function AtlasTab({ persons }: { persons: Person[] }) {
  const veil = useVeil()
  const [markers, setMarkers] = useState<Marker[]>([])
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [zoom, setZoom] = useState(3)
  const [center, setCenter] = useState<{ lat: number; lon: number }>({ lat: 30, lon: 0 })
  const [size, setSize] = useState({ w: 900, h: 560 })
  const [selected, setSelected] = useState<Marker | null>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const drag = useRef<{ x: number; y: number; cx: number; cy: number } | null>(null)

  // collect unique places → geocode (server cache makes repeats free)
  useEffect(() => {
    const wanted: { place: string; kind: Marker['kind']; label: string }[] = []
    for (const p of persons) {
      const name = maskName(p, veil)
      if (p.birth_place) wanted.push({ place: p.birth_place, kind: 'birth', label: `${name} — born` })
      if (p.death_place) wanted.push({ place: p.death_place, kind: 'death', label: `${name} — passed` })
      const burial = [p.burial_cemetery, p.burial_place].filter(Boolean).join(', ')
      if (burial) wanted.push({ place: burial, kind: 'burial', label: `${name} — at rest` })
    }
    if (!wanted.length) return
    setProgress({ done: 0, total: wanted.length })

    let cancelled = false
    ;(async () => {
      const out: Marker[] = []
      const geoCache = new Map<string, { lat: number; lon: number } | null>()
      for (const w of wanted) {
        if (cancelled) return
        const key = w.place.toLowerCase()
        if (!geoCache.has(key)) {
          try {
            const res = await api.geocode(w.place)
            geoCache.set(key, res.found ? { lat: res.lat!, lon: res.lon! } : null)
          } catch { geoCache.set(key, null) }
        }
        const hit = geoCache.get(key)
        if (hit) out.push({ ...hit, kind: w.kind, label: w.label, place: w.place })
        setProgress(p => ({ ...p, done: p.done + 1 }))
        setMarkers([...out])
      }
      if (out.length) {
        const lats = out.map(m => m.lat), lons = out.map(m => m.lon)
        const cLat = (Math.min(...lats) + Math.max(...lats)) / 2
        const cLon = (Math.min(...lons) + Math.max(...lons)) / 2
        const span = Math.max(Math.max(...lons) - Math.min(...lons), (Math.max(...lats) - Math.min(...lats)) * 1.4, 0.5)
        setCenter({ lat: cLat, lon: cLon })
        setZoom(Math.max(2, Math.min(11, Math.floor(Math.log2(360 / span)) + 1)))
      }
    })()
    return () => { cancelled = true }
  }, [persons, veil]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = boxRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // visible tile grid
  const view = useMemo(() => {
    const cx = lonToX(center.lon, zoom)
    const cy = latToY(center.lat, zoom)
    const originX = cx - size.w / 2
    const originY = cy - size.h / 2
    const x0 = Math.floor(originX / TILE), y0 = Math.floor(originY / TILE)
    const x1 = Math.floor((originX + size.w) / TILE), y1 = Math.floor((originY + size.h) / TILE)
    const max = Math.pow(2, zoom)
    const tiles: { x: number; y: number; left: number; top: number; url: string }[] = []
    for (let tx = x0; tx <= x1; tx++) {
      for (let ty = Math.max(0, y0); ty <= Math.min(max - 1, y1); ty++) {
        const wx = ((tx % max) + max) % max
        tiles.push({
          x: tx, y: ty,
          left: tx * TILE - originX, top: ty * TILE - originY,
          url: `https://tile.openstreetmap.org/${zoom}/${wx}/${ty}.png`,
        })
      }
    }
    return { tiles, originX, originY }
  }, [center, zoom, size])

  const onDown = (e: React.PointerEvent) => {
    drag.current = { x: e.clientX, y: e.clientY, cx: lonToX(center.lon, zoom), cy: latToY(center.lat, zoom) }
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
  }
  const onMove = (e: React.PointerEvent) => {
    if (!drag.current) return
    const nx = drag.current.cx - (e.clientX - drag.current.x)
    const ny = drag.current.cy - (e.clientY - drag.current.y)
    const max = Math.pow(2, zoom) * TILE
    const lon = (nx / max) * 360 - 180
    const n = Math.PI - (2 * Math.PI * ny) / max
    const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))
    setCenter({ lat: Math.max(-85, Math.min(85, lat)), lon })
  }
  const onUp = () => { drag.current = null }

  if (progress.total === 0) {
    return (
      <div className="card-glass p-12 text-center text-slate-500 text-sm">
        <div className="text-4xl mb-3">🗺️</div>
        No places recorded yet — add birthplaces, death places, or resting places and the family atlas draws itself.
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex gap-3 text-[11px] text-slate-400">
          {Object.entries(KIND_STYLE).map(([k, s]) => (
            <span key={k} className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} /> {s.icon} {k}
            </span>
          ))}
        </div>
        {progress.done < progress.total && (
          <span className="text-[11px] text-slate-500">charting {progress.done}/{progress.total} places…</span>
        )}
      </div>

      <div ref={boxRef}
        className="relative w-full rounded-2xl overflow-hidden border border-white/[0.07] select-none touch-none"
        style={{ height: 'min(62vh, 640px)', cursor: drag.current ? 'grabbing' : 'grab', background: '#0d0a1c' }}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
        {view.tiles.map(t => (
          <img key={`${zoom}/${t.x}/${t.y}`} src={t.url} alt=""
            className="absolute pointer-events-none"
            style={{ left: t.left, top: t.top, width: TILE, height: TILE, filter: 'saturate(0.85) brightness(0.92)' }}
            loading="lazy" draggable={false} />
        ))}
        {markers.map((m, i) => {
          const left = lonToX(m.lon, zoom) - view.originX
          const top = latToY(m.lat, zoom) - view.originY
          if (left < -30 || top < -30 || left > size.w + 30 || top > size.h + 30) return null
          const s = KIND_STYLE[m.kind]
          return (
            <button key={i} onClick={() => setSelected(m)}
              className="absolute -translate-x-1/2 -translate-y-1/2 z-10"
              style={{ left, top }} title={`${m.label} · ${m.place}`}>
              <span className="block w-3.5 h-3.5 rounded-full border-2 border-void-950 shadow-lg node-pulse"
                style={{ backgroundColor: s.color }} />
            </button>
          )
        })}

        {/* zoom controls + attribution */}
        <div className="absolute top-3 right-3 flex flex-col gap-1.5 z-20">
          <button onClick={() => setZoom(z => Math.min(12, z + 1))}
            className="w-8 h-8 rounded-lg bg-void-900/90 border border-white/10 text-slate-200">+</button>
          <button onClick={() => setZoom(z => Math.max(2, z - 1))}
            className="w-8 h-8 rounded-lg bg-void-900/90 border border-white/10 text-slate-200">−</button>
        </div>
        <span className="absolute bottom-1 right-2 text-[9px] text-slate-400/80 bg-void-950/60 px-1 rounded z-20">
          © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer" className="underline">OpenStreetMap</a>
        </span>

        {selected && (
          <div className="absolute left-3 bottom-3 z-20 card-glass !bg-void-900/95 px-3.5 py-2.5 max-w-[280px]">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm text-slate-100">{KIND_STYLE[selected.kind].icon} {selected.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{selected.place}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-slate-300">✕</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
